import { useState, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { senderApi, authApi } from '../services/api'
import Loading from '../components/Loading'
import ErrorMessage from '../components/ErrorMessage'
import SubmitSuccess from '../components/SubmitSuccess'
import { compressImage, validateImageFile, validateImageSize, validatePhotoCount } from '../utils/imageCompress'
import { uploadToOss } from '../utils/ossUpload'
import type { Order } from '../types'

interface ContactInfo {
  name: string
  phone: string
  address: string
}

const carriers = [
  '顺丰速运',
  '中通快递',
  '圆通快递',
  '申通快递',
  '韵达快递',
  '京东物流',
  '德邦快递',
  '其他',
]

function validateTrackingNo(trackingNo: string, orderNo?: string | null): string | null {
  if (!trackingNo) return null
  const cleaned = trackingNo.replace(/[\s\-_]/g, '').toUpperCase()

  // 1. 是否以订单号前缀开头（wa/tb/tc/QX）
  if (/^(WA|TB|TC|QX)/.test(cleaned)) {
    return '看起来像订单号，请确认是否为正确的快递单号'
  }

  // 2. 是否与当前订单号的数字部分一致
  if (orderNo) {
    const orderDigits = orderNo.replace(/[^0-9]/g, '')
    const trackingDigits = cleaned.replace(/[^0-9]/g, '')
    if (orderDigits.length >= 6 && trackingDigits === orderDigits) {
      return '看起来像订单号，请确认是否为正确的快递单号'
    }
  }

  return null
}

export default function Sender() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [step, setStep] = useState<'input' | 'verify' | 'form' | 'success'>('input')
  const [inputToken, setInputToken] = useState(token || '')
  const [order, setOrder] = useState<Order | null>(null)
  const [carrier, setCarrier] = useState('')
  const [carrierDropdownOpen, setCarrierDropdownOpen] = useState(false)
  const [trackingNo, setTrackingNo] = useState('')
  const [photos, setPhotos] = useState<string[]>([])
  const [remark, setRemark] = useState('')
  const [specs, setSpecs] = useState<Array<{ type: string; material: string; quantity: number }>>([{ type: '', material: '', quantity: 1 }])
  const [specDropdownOpen, setSpecDropdownOpen] = useState<string | null>(null)

  const specTypeOptions = ['单路开关', '双路开关', '五孔插座', '英标插座', '万能插座', 'USB 插座', '网口插座', '五孔地插/封闭面板']
  const specMaterialOptions = ['岩板', '瓷砖', '木饰面', '木板', '大理石', '玻璃', '金属板']
  const [contactInfo, setContactInfo] = useState<ContactInfo>({ name: '', phone: '', address: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [trackingWarning, setTrackingWarning] = useState('')
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Phone login state
  const [loginPhone, setLoginPhone] = useState('')
  const [loginCode, setLoginCode] = useState('')
  const [loginName, setLoginName] = useState('')
  const [loginCountdown, setLoginCountdown] = useState(0)

  const verifyToken = async () => {
    if (!inputToken.trim()) {
      setError('请输入访问口令')
      return
    }
    setLoading(true)
    setError('')
    try {
      const data = await senderApi.getOrder(inputToken.trim())
      setOrder(data as Order)
      const orderData = data as any
      if (orderData.contactName) setContactInfo(prev => ({ ...prev, name: orderData.contactName }))
      if (orderData.contactPhone) setContactInfo(prev => ({ ...prev, phone: orderData.contactPhone }))
      if (orderData.contactAddress) setContactInfo(prev => ({ ...prev, address: orderData.contactAddress }))
      setStep('verify')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '验证失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const countError = validatePhotoCount(photos.length, files.length)
    if (countError) {
      setError(countError)
      e.target.value = ''
      return
    }

    if (!order?.id) {
      setError('订单信息未加载，请刷新页面')
      e.target.value = ''
      return
    }

    setUploading(true)
    setError('')

    const newPhotos: string[] = []
    for (const file of Array.from(files)) {
      const validation = validateImageFile(file)
      if (validation) {
        setError(validation)
        continue
      }
      const sizeError = validateImageSize(file)
      if (sizeError) {
        setError(sizeError)
        continue
      }
      try {
        const compressed = await compressImage(file)
        const fileName = file.name.replace(/\.[^.]+$/, '') + '.jpg'
        const uploaded = await uploadToOss(
          compressed.blob,
          fileName,
          { orderId: order.id, type: 'material_photo' },
          { width: compressed.width, height: compressed.height },
        )
        newPhotos.push(uploaded.url)
        // 移动端 Canvas 内存释放缓冲，防止连续压缩耗尽 GPU 纹理内存
        await new Promise(r => setTimeout(r, 150))
      } catch (err) {
        const msg = err instanceof Error ? err.message : '上传失败'
        setError(`图片上传失败: ${msg}`)
      }
    }

    if (newPhotos.length > 0) {
      setPhotos(prev => [...prev, ...newPhotos])
    }

    setUploading(false)
    e.target.value = ''
  }

  const submit = async () => {
    if (!contactInfo.name.trim() || !contactInfo.phone.trim() || !contactInfo.address.trim()) {
      setError('请完整填写您的收件信息（姓名+电话+地址）')
      return
    }
    if (!carrier || !trackingNo) {
      setError('请填写完整的快递信息')
      return
    }

    // 软校验：不像是快递单号时弹确认框，用户确认后继续提交
    const warn = validateTrackingNo(trackingNo.trim().toUpperCase(), order?.orderNo)
    if (warn && !window.confirm(warn + '\n\n是否继续提交？')) {
      return
    }

    if (photos.length === 0) {
      setError('请上传材料照片')
      return
    }

    setLoading(true)
    setError('')
    try {
      await senderApi.submitShipment(inputToken, {
        carrier,
        trackingNo,
        photos,
        senderName: contactInfo.name.trim(),
        senderAddress: contactInfo.address.trim(),
        remark: remark.trim() || undefined,
        specs: specs.filter(s => s.type && s.material).map(s => ({ type: s.type.trim(), material: s.material.trim(), quantity: s.quantity })),
      })
      setStep('success')
      window.scrollTo(0, 0)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '提交失败'
      if (message.includes('已关闭')) {
        setError('订单已关闭，无法提交快递信息')
      } else {
        setError(message)
      }
    } finally {
      setLoading(false)
    }
  }

  const sendLoginCode = async () => {
    if (!loginPhone.trim() || !/^1[3-9]\d{9}$/.test(loginPhone.trim())) {
      setError('请输入正确的手机号')
      return
    }
    setLoading(true)
    try {
      await authApi.sendSms(loginPhone.trim())
      setError('')
      setLoginCountdown(60)
      const timer = setInterval(() => {
        setLoginCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '发送失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async () => {
    if (!loginPhone.trim() || !loginCode.trim()) {
      setError('请填写手机号和验证码')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await authApi.verifyPhone({
        phone: loginPhone.trim(),
        code: loginCode.trim(),
        name: loginName.trim() || undefined,
      }) as { token: string }
      localStorage.setItem('sender_token', res.token)
      setLoginPhone('')
      setLoginCode('')
      setLoginName('')
      setError('')
      // 登录成功后如果已有口令，自动继续验证流程
      if (inputToken.trim()) {
        await verifyToken()
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '登录失败'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('sender_token')
    setStep('input')
    setInputToken('')
  }

  // Not logged in — show phone login form
  if (!localStorage.getItem('sender_token')) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
        <div className="nav-bar">
          <button onClick={() => navigate('/')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
            寄件人登录
          </h1>
          <div style={{ width: 20 }} />
        </div>

        <div style={{ padding: '24px' }}>
          <div style={{ textAlign: 'center', fontSize: 10, color: '#ccc', marginBottom: 8 }}>v2.1</div>
          <div className="card">
            <p style={{ fontSize: 13, color: '#9a9a9a', marginBottom: 16, letterSpacing: '0.15em' }}>
              请先使用手机号登录后再提交快递信息
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>姓名</label>
                <input
                  type="text"
                  value={loginName}
                  onChange={(e) => setLoginName(e.target.value)}
                  placeholder="请输入您的姓名（首次登录时填写）"
                  className="input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>手机号</label>
                <input
                  type="tel"
                  value={loginPhone}
                  onChange={(e) => setLoginPhone(e.target.value)}
                  placeholder="请输入手机号"
                  className="input"
                  maxLength={11}
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>验证码</label>
                  <input
                    type="text"
                    value={loginCode}
                    onChange={(e) => setLoginCode(e.target.value)}
                    placeholder="验证码"
                    className="input"
                    maxLength={6}
                  />
                </div>
                <button
                  onClick={sendLoginCode}
                  disabled={loading || loginCountdown > 0}
                  className="btn-secondary"
                  style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap', fontSize: 13 }}
                >
                  {loginCountdown > 0 ? `${loginCountdown}秒后重发` : '获取验证码'}
                </button>
              </div>
              {error && (
                <p style={{ fontSize: 13, color: '#313131', letterSpacing: '0.15em' }}>{error}</p>
              )}
              <button onClick={handleLogin} disabled={loading} className="btn-primary">
                {loading ? '登录中' : '登录'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'input') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
        <div className="nav-bar">
          <button onClick={() => navigate('/')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
            寄件人入口
          </h1>
          {localStorage.getItem('sender_token') ? (
            <button
              onClick={handleLogout}
              style={{ color: '#d9363e', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, letterSpacing: '0.15em' }}
            >
              退出登录
            </button>
          ) : (
            <div style={{ width: 36 }} />
          )}
        </div>

        <div style={{ padding: '24px' }}>
          <div className="card">
            <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 12, letterSpacing: '0.15em' }}>
              访问口令
            </label>
            <input
              type="text"
              value={inputToken}
              onChange={(e) => setInputToken(e.target.value)}
              placeholder="请输入系统提供的访问口令"
              className="input"
              onKeyDown={(e) => e.key === 'Enter' && verifyToken()}
            />
            <p style={{ fontSize: 13, color: '#9a9a9a', marginTop: 12, letterSpacing: '0.15em' }}>
              访问口令由客户在订单详情页生成后分享给您
            </p>
          </div>

          {error && <ErrorMessage message={error} onRetry={() => setError('')} />}
          {loading && <Loading />}

          <button onClick={verifyToken} disabled={loading} className="w-full btn-primary" style={{ marginTop: 24 }}>
            {loading ? '验证中' : '验证口令'}
          </button>
        </div>
      </div>
    )
  }

  if (step === 'verify' && order) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
        <div className="nav-bar">
          <button onClick={() => setStep('input')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
            确认订单
          </h1>
          {localStorage.getItem('sender_token') ? (
            <button
              onClick={handleLogout}
              style={{ color: '#d9363e', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, letterSpacing: '0.15em' }}
            >
              退出登录
            </button>
          ) : (
            <div style={{ width: 36 }} />
          )}
        </div>

        <div style={{ padding: '24px' }}>
          <div className="card">
            <h3 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em', marginBottom: 16 }}>
              订单信息
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, letterSpacing: '0.15em' }}>
                <span style={{ color: '#9a9a9a' }}>订单号</span>
                <span style={{ fontWeight: 500, color: '#313131' }}>{order.orderNo}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, letterSpacing: '0.15em' }}>
                <span style={{ color: '#9a9a9a' }}>客户</span>
                <div style={{ textAlign: 'right', color: '#313131', fontSize: 14 }}>
                  <div>{order.customer?.name || order.customerName || '-'}</div>
                  <div style={{ color: '#9a9a9a', fontSize: 13 }}>{order.customer?.phone || order.customerPhone || '-'}</div>
                  <div style={{ color: '#9a9a9a', fontSize: 13 }}>{order.address || '-'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, letterSpacing: '0.15em' }}>
                <span style={{ color: '#9a9a9a' }}>规格</span>
                <div style={{ textAlign: 'right', color: '#313131', fontSize: 14 }}>
                  {Array.isArray(order.specs) && order.specs.length > 0 ? order.specs.map((s: any, i: number) => (
                    <div key={i}>{s.type} / {s.material} &times; {s.quantity}</div>
                  )) : <span style={{ color: '#9a9a9a' }}>-</span>}
                </div>
              </div>
            </div>
          </div>

          <p style={{ fontSize: 15, color: '#9a9a9a', marginTop: 24, marginBottom: 16, letterSpacing: '0.15em' }}>
            请确认以上订单信息，确认无误后提交快递单号。
          </p>

          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setStep('input')} className="flex-1 btn-secondary">
              不是我的订单
            </button>
            <button onClick={() => setStep('form')} className="flex-1 btn-primary">
              确认，提交快递
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'form') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#eeeeee' }}>
        <div className="nav-bar">
          <button onClick={() => setStep('verify')} style={{ color: '#9a9a9a', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em' }}>
            提交快递信息
          </h1>
          {localStorage.getItem('sender_token') ? (
            <button
              onClick={handleLogout}
              style={{ color: '#d9363e', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, letterSpacing: '0.15em' }}
            >
              退出登录
            </button>
          ) : (
            <div style={{ width: 36 }} />
          )}
        </div>

        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
          />

          <div className="card">
            <h3 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em', marginBottom: 16 }}>
              收件信息
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="text"
                value={contactInfo.name}
                placeholder="收件人姓名"
                className="input"
                disabled
                readOnly
              />
              <input
                type="tel"
                value={contactInfo.phone}
                placeholder="收件人电话"
                className="input"
                maxLength={11}
                disabled
                readOnly
              />
              <input
                type="text"
                value={contactInfo.address}
                placeholder="收件地址"
                className="input"
                disabled
                readOnly
              />
            </div>
          </div>

          <div className="card">
            <h3 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em', marginBottom: 16 }}>
              快递信息
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>
                  快递公司
                </label>
                <div
                  onClick={() => setCarrierDropdownOpen(!carrierDropdownOpen)}
                  style={{
                    width: '100%',
                    padding: '12px 0',
                    fontSize: 15,
                    fontFamily: '"Noto Sans SC", "Inter", sans-serif',
                    color: carrier ? '#313131' : '#9a9a9a',
                    border: 'none',
                    borderBottom: '1px solid #dddddd',
                    outline: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    letterSpacing: '0.15em',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>{carrier || '请选择快递公司'}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2" style={{ transform: carrierDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>
                {carrierDropdownOpen && (
                  <div style={{ backgroundColor: '#fff', border: '1px solid #dddddd', marginTop: 4, maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    <div
                      onClick={() => { setCarrier(''); setCarrierDropdownOpen(false); setTrackingWarning('') }}
                      style={{ padding: '10px 12px', fontSize: 14, color: '#9a9a9a', cursor: 'pointer', letterSpacing: '0.15em' }}
                    >
                      请选择快递公司
                    </div>
                    {carriers.map(c => (
                      <div
                        key={c}
                        onClick={() => { setCarrier(c); setCarrierDropdownOpen(false); const warn = validateTrackingNo(trackingNo.trim().toUpperCase(), order?.orderNo); setTrackingWarning(warn || '') }}
                        style={{ padding: '10px 12px', fontSize: 14, color: '#313131', cursor: 'pointer', letterSpacing: '0.15em', borderTop: '1px solid #f0f0f0', fontWeight: carrier === c ? 500 : 400 }}
                      >
                        {c}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>
                  快递单号（材料寄出单号）
                </label>
                <input
                  type="text"
                  maxLength={30}
                  value={trackingNo}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\s/g, '').toUpperCase()
                    setTrackingNo(v)
                    // 实时校验
                    const warn = validateTrackingNo(v, order?.orderNo)
                    setTrackingWarning(warn || '')
                  }}
                  onBlur={() => {
                    const warn = validateTrackingNo(trackingNo.trim().toUpperCase(), order?.orderNo)
                    setTrackingWarning(warn || '')
                  }}
                  placeholder="请输入快递单号，非订单号"
                  style={{ borderColor: trackingWarning ? '#ff4d4f' : undefined }}
                  className="input"
                />
                <div style={{ fontSize: 12, marginTop: 4, minHeight: 18 }}>
                  {trackingWarning ? (
                    <span style={{ display: 'inline-block', color: '#ff4d4f', padding: '6px 10px', backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4, fontWeight: 500 }}>
                      {trackingWarning}
                    </span>
                  ) : (
                    <span style={{ color: '#ccc' }}>v2</span>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>
                  定制规格
                </label>
                {specs.map((spec, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                      <div
                        onClick={(e) => { e.stopPropagation(); setSpecDropdownOpen(specDropdownOpen === `type-${i}` ? null : `type-${i}`) }}
                        style={{
                          width: '100%',
                          padding: '12px 0',
                          fontSize: 15,
                          fontFamily: '"Noto Sans SC", "Inter", sans-serif',
                          color: spec.type ? '#313131' : '#9a9a9a',
                          border: 'none',
                          borderBottom: '1px solid #dddddd',
                          outline: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          letterSpacing: '0.15em',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{spec.type || '类型'}</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2" style={{ flexShrink: 0, marginLeft: 4, transform: specDropdownOpen === `type-${i}` ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </div>
                      {specDropdownOpen === `type-${i}` && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, backgroundColor: '#fff', border: '1px solid #dddddd', maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                          {specTypeOptions.map(t => (
                            <div
                              key={t}
                              onClick={() => {
                                const next = [...specs]
                                next[i] = { ...next[i], type: t }
                                setSpecs(next)
                                setSpecDropdownOpen(null)
                              }}
                              style={{ padding: '10px 12px', fontSize: 14, color: '#313131', cursor: 'pointer', letterSpacing: '0.15em', borderTop: '1px solid #f0f0f0', fontWeight: spec.type === t ? 500 : 400 }}
                            >
                              {t}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                      <div
                        onClick={(e) => { e.stopPropagation(); setSpecDropdownOpen(specDropdownOpen === `material-${i}` ? null : `material-${i}`) }}
                        style={{
                          width: '100%',
                          padding: '12px 0',
                          fontSize: 15,
                          fontFamily: '"Noto Sans SC", "Inter", sans-serif',
                          color: spec.material ? '#313131' : '#9a9a9a',
                          border: 'none',
                          borderBottom: '1px solid #dddddd',
                          outline: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          letterSpacing: '0.15em',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{spec.material || '材质'}</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2" style={{ flexShrink: 0, marginLeft: 4, transform: specDropdownOpen === `material-${i}` ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </div>
                      {specDropdownOpen === `material-${i}` && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, backgroundColor: '#fff', border: '1px solid #dddddd', maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                          {specMaterialOptions.map(m => (
                            <div
                              key={m}
                              onClick={() => {
                                const next = [...specs]
                                next[i] = { ...next[i], material: m }
                                setSpecs(next)
                                setSpecDropdownOpen(null)
                              }}
                              style={{ padding: '10px 12px', fontSize: 14, color: '#313131', cursor: 'pointer', letterSpacing: '0.15em', borderTop: '1px solid #f0f0f0', fontWeight: spec.material === m ? 500 : 400 }}
                            >
                              {m}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="number"
                      value={spec.quantity}
                      onChange={(e) => {
                        const next = [...specs]
                        next[i] = { ...next[i], quantity: parseInt(e.target.value) || 1 }
                        setSpecs(next)
                      }}
                      className="input"
                      min={1}
                      style={{ width: 60, minWidth: 60, textAlign: 'center' }}
                    />
                    {specs.length > 1 && (
                      <button
                        onClick={() => setSpecs(prev => prev.filter((_, idx) => idx !== i))}
                        style={{
                          width: 28, height: 28, border: '1px solid #dddddd',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#9a9a9a', background: 'none', cursor: 'pointer', flexShrink: 0,
                          fontSize: 14,
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setSpecs(prev => [...prev, { type: '', material: '', quantity: 1 }])}
                  style={{
                    fontSize: 12, color: '#313131', background: 'none', border: '1px solid #dddddd',
                    padding: '6px 12px', cursor: 'pointer', letterSpacing: '0.15em',
                  }}
                >
                  + 添加规格
                </button>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>
                  材料照片（必填）
                </label>
                <div style={{ marginBottom: 12, padding: 12, border: '1px solid #dddddd', backgroundColor: '#fafafa' }}>
                  <p style={{ fontSize: 11, color: '#9a9a9a', marginBottom: 8, letterSpacing: '0.15em' }}>参考示例</p>
                  <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                    <img src={`${import.meta.env.BASE_URL}images/wood-mark.jpg`} alt="木料标记示例" style={{ width: 80, height: 80, objectFit: 'cover', border: '1px solid #dddddd', flexShrink: 0 }} />
                    <img src={`${import.meta.env.BASE_URL}images/stone-mark.jpg`} alt="石材标记示例" style={{ width: 80, height: 80, objectFit: 'cover', border: '1px solid #dddddd', flexShrink: 0 }} />
                    <img src={`${import.meta.env.BASE_URL}images/direction-mark.jpg`} alt="方位标记示例" style={{ width: 80, height: 80, objectFit: 'cover', border: '1px solid #dddddd', flexShrink: 0 }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {photos.map((photo, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={photo} alt="" style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid #dddddd' }} />
                      <button
                        onClick={() => {
                          setPhotos(prev => prev.filter((_, idx) => idx !== i))
                        }}
                        style={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          width: 16,
                          height: 16,
                          backgroundColor: '#313131',
                          color: '#fff',
                          border: 'none',
                          fontSize: 10,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    style={{
                      width: 64,
                      height: 64,
                      border: '1px dashed #dddddd',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#9a9a9a',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <span style={{ fontSize: 11, marginTop: 2, letterSpacing: '0.15em' }}>上传</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 style={{ fontFamily: '"Noto Sans SC", sans-serif', fontSize: 16, fontWeight: 500, color: '#313131', letterSpacing: '0.15em', marginBottom: 16 }}>
              备注（选填）
            </h3>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="如有需要提醒的信息请在此备注"
              className="input"
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>

          {error && <ErrorMessage message={error} onRetry={() => setError('')} />}
          {loading && <Loading />}

          <button onClick={submit} disabled={loading} className="w-full btn-primary">
            {loading ? '提交中' : '确认提交'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <SubmitSuccess orderNo={order?.orderNo} />
  )
}
